
import React, { useState } from "react";
import { X, Lightbulb } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ContextualHelpProps {
  title: string;
  content: string;
  type?: "tip" | "guidance" | "example";
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const ContextualHelp: React.FC<ContextualHelpProps> = ({
  title,
  content,
  type = "tip",
  dismissible = true,
  onDismiss,
  className
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (!isVisible) return null;

  const typeStyles = {
    tip: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30",
    guidance: "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30",
    example: "border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30"
  };

  const iconColors = {
    tip: "text-blue-600 dark:text-blue-400",
    guidance: "text-green-600 dark:text-green-400",
    example: "text-purple-600 dark:text-purple-400"
  };

  return (
    <Card className={cn(typeStyles[type], "relative", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Lightbulb className={cn("h-4 w-4", iconColors[type])} />
          {title}
          {dismissible && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-6 w-6 p-0 hover:bg-transparent"
              onClick={handleDismiss}
              aria-label="Dismiss help"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground">{content}</p>
      </CardContent>
    </Card>
  );
};

export default ContextualHelp;
